import { ThemeProvider } from "@emotion/react";
import { CaptureConsole, ExtraErrorData } from "@sentry/integrations";
import * as Sentry from "@sentry/react";
import { NavbarWrapper } from "components/NavbarV2/NavbarWrapper";
import Home from "pages/Home/Home";
import React from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { Web3 } from "./components/Web3/Web3";
import { BEBOP_ROUTES } from "./config/consts";
import { NotFound } from "./pages/404/404";
import { Trade } from "./pages/Trade/Trade";
import { GlobalStore } from "./store/global/GlobalStore";
import { getEnvString } from "./utils/sentryUtils";
import { AppWrapper } from "./wrappers/AppWrapper/AppWrapper";
import { isProd } from "./config/env";
import { RegionRestrictedModal } from "./components/Modal/instance/RegionRestrictedModal/RegionRestrictedModal";
import { Activity } from "./pages/TradeHistory/Activity";
import { SomethingWentWrong } from "pages/ErrorBoundary/ErrorBoundary";

if (isProd) {
  Sentry.init({
    dsn: "https://e5b2f20d3add4c389d903067480b4a58@sentry.bebop.xyz/2",
    environment: getEnvString(),
    release: APP_VERSION,
    enabled: isProd,
    normalizeDepth: 15,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        ),
      }),
      // Serialize objects going to sentry down to 5 levels.
      new ExtraErrorData({ depth: 15 }),
      new CaptureConsole(),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.5,
    replaysOnErrorSampleRate: 1.0,
  });
}

export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
const queryClient = new QueryClient();

const App: React.FC = () => {
  const theme = GlobalStore.useState(s => s.theme);
  const [caughtError, setCaughtError] = React.useState<Error | null>(null);
  const [componentStack, setComponentStack] = React.useState<string | null>(null);
  const location = window.location;
  console.log(`Location: ${location.pathname}`);
  return (
    <Sentry.ErrorBoundary
      fallback={<SomethingWentWrong error={caughtError} stack={componentStack} />}
      showDialog
      onError={(error: Error, componentStack: string) => {
        setCaughtError(error);
        setComponentStack(componentStack);
      }}
    >
      <Web3>
        <BrowserRouter>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
              <AppWrapper>
                <NavbarWrapper
                  isVisible={
                    !(
                      location.pathname === BEBOP_ROUTES.PageNotFound ||
                      location.pathname === BEBOP_ROUTES.Wildcard
                    )
                  }
                >
                  <RegionRestrictedModal />
                  <SentryRoutes>
                    <Route path="/" element={<Home />} />
                    <Route path={BEBOP_ROUTES.Trade} element={<Trade />} />
                    <Route path={BEBOP_ROUTES.Activity} element={<Activity />} />
                    {/* TODO: Minor - Figure out why this doesn't load when navigated directly  */}
                    <Route path={BEBOP_ROUTES.PageNotFound} element={<NotFound />} />
                    <Route
                      path={BEBOP_ROUTES.Wildcard}
                      element={<Navigate to={BEBOP_ROUTES.PageNotFound} replace />}
                    />
                  </SentryRoutes>
                </NavbarWrapper>
              </AppWrapper>
            </QueryClientProvider>
          </ThemeProvider>
        </BrowserRouter>
      </Web3>
    </Sentry.ErrorBoundary>
  );
};

export default Sentry.withProfiler(App);
